import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-light-svg-icons/faChevronUp';
import { TypedMemo } from '../../../common/types';
import { ServiceExtraItem, serviceExtrasOpen } from './serviceExtraTypes';
import { ServiceExtraItemView, ServiceExtraName } from './ServiceExtraItemView';
import { extrasMessages as m } from './extrasMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the extras box; each part uses its own slice of it */
import styles from './ServiceExtras.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ServiceExtrasProps {
  /** The mono label of the box, for example "Spotify extras". */
  label: string;
  items: readonly ServiceExtraItem[];
  testId?: string;
}

/**
 * The extras box under the link of a service row (design spotify-canvas d3, d4, option B; one part
 * for every service, design service-extras a0): its own edge and the left-aligned label
 * "<Service> extras" say that it is not a service. Open when an item waits for the artist, else
 * folded to one line with every item. Hide / Show is per page view. Generic: a service gives its
 * items (Spotify: the Canvas; YouTube: the full-song videos; SoundCloud: the song).
 */
const ServiceExtrasComponent: React.FC<ServiceExtrasProps> = ({ label, items, testId }) => {
  const intl = useIntl();
  const [choice, setChoice] = useState<boolean | null>(null);
  if (items.length === 0) {
    return null;
  }
  const open = serviceExtrasOpen(items, choice);

  if (!open) {
    // Folded (design service-extras a1 .se-sum): ONE line with the label, each item and Show.
    return (
      <div className={styles.box} data-testid={testId}>
        <div className={styles.summary}>
          <span className={styles.label}>{label}</span>
          {items.map((item) => (
            <span key={item.id} className={styles.summaryItem} data-testid={`service-extra-${item.id}`}>
              {item.thumb !== undefined && <span className={styles.thumb}>{item.thumb}</span>}
              <ServiceExtraName title={item.title} chips={item.chips} />
            </span>
          ))}
          <button type="button" className={styles.toggle} aria-expanded={false} onClick={() => setChoice(true)}>
            {intl.formatMessage(m.show)}
            <FontAwesomeIcon icon={faChevronDown} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.box} data-testid={testId}>
      <div className={styles.head}>
        <span className={styles.label}>{label}</span>
        <button type="button" className={styles.toggle} aria-expanded onClick={() => setChoice(false)}>
          {intl.formatMessage(m.hide)}
          <FontAwesomeIcon icon={faChevronUp} />
        </button>
      </div>
      {items.map((item) => (
        <ServiceExtraItemView key={item.id} item={item} />
      ))}
    </div>
  );
};

export const ServiceExtras = TypedMemo(ServiceExtrasComponent);
